import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineHealthAndSafety,
  MdOutlineSubtitles,
  MdOutlineWorkOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  RELATIONSHIP,
  SOCIAL_REGIMES,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Partner = ({
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const birthDate = get(
    initialValues,
    "spouse.identity.user.birthDate",
    undefined,
  );

  const lastnamePath = "spouse.identity.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const birthNamePath = "spouse.identity.user.birthName";
  const birthNameField = bindField(birthNamePath, {
    label: SUBSCRIPTION.FORM.BIRTH_NAME.LABEL,
    required: isFieldRequired(birthNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "spouse.identity.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const birthDatePath = "spouse.identity.user.birthDate";
  const birthDateField = bindField(birthDatePath, {
    label: SUBSCRIPTION.FORM.BIRTH_DATE.LABEL,
    required: isFieldRequired(birthDatePath),
    disabled: true,
  });

  const socioProfessionalCategoryPath =
    "spouse.identity.socioProfessionalCategory";
  const socioProfessionalCategoryField = bindField(
    socioProfessionalCategoryPath,
    {
      label: SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.LABEL,
      required: isFieldRequired(socioProfessionalCategoryPath),
      onBlur: () => setPercent(initialValues),
    },
  );

  const socialRegimePath = "spouse.identity.socialRegime";
  const socialRegimeField = bindField(socialRegimePath, {
    label: SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL,
    required: isFieldRequired(socialRegimePath),
    onBlur: () => setPercent(initialValues),
  });

  const socialSecurityNumberPath = "spouse.identity.socialSecurityNumber";
  const socialSecurityNumberField = bindField(socialSecurityNumberPath, {
    label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
    required: isFieldRequired(socialSecurityNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.SPOUSE,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <FormField
                path={lastnameField.path}
                label={lastnameField.label}
                required={lastnameField.required}
                disabled={lastnameField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                    label={lastnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={lastnameField.defaultValue}
                    {...field}
                    onChange={lastnameField.onChange}
                    onBlur={lastnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={birthNameField.path}
                label={birthNameField.label}
                required={birthNameField.required}
                disabled={birthNameField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={SUBSCRIPTION.FORM.BIRTH_NAME.PLACEHOLDER}
                    label={birthNameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={birthNameField.defaultValue}
                    {...field}
                    onChange={birthNameField.onChange}
                    onBlur={birthNameField.onBlur}
                    optional={!birthNameField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={firstnameField.path}
                label={firstnameField.label}
                required={firstnameField.required}
                disabled={firstnameField.disabled}
              >
                {(field) => (
                  <Input
                    label={firstnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={firstnameField.defaultValue}
                    {...field}
                    onChange={firstnameField.onChange}
                    onBlur={firstnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={birthDateField.path}
                label={birthDateField.label}
                required={birthDateField.required}
                disabled={birthDateField.disabled}
              >
                {(field) => (
                  <Input
                    label={birthDateField.label}
                    icon={<MdOutlineCake size={16} />}
                    placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                    defaultValue={birthDate && dayjs(birthDate)}
                    type="date"
                    variant="simple"
                    format={DATE_FORMAT}
                    {...field}
                    onChange={birthDateField.onChange}
                    onBlur={birthDateField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socioProfessionalCategoryField.path}
                label={socioProfessionalCategoryField.label}
                required={socioProfessionalCategoryField.required}
                disabled={socioProfessionalCategoryField.disabled}
              >
                {(field) => (
                  <Select
                    type="simple"
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    label={socioProfessionalCategoryField.label}
                    icon={<MdOutlineWorkOutline size={16} />}
                    placeholder={
                      SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.PLACEHOLDER
                    }
                    options={Object.entries(SOCIO_PROFESSIONAL_CATEGORIES).map(
                      ([key, value]) => ({
                        value,
                        label:
                          SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY
                            .OPTIONS[key],
                      }),
                    )}
                    defaultValue={socioProfessionalCategoryField.defaultValue}
                    {...field}
                    onChange={socioProfessionalCategoryField.onChange}
                    onBlur={socioProfessionalCategoryField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socialRegimeField.path}
                label={socialRegimeField.label}
                required={socialRegimeField.required}
                disabled={socialRegimeField.disabled}
              >
                {(field) => (
                  <Select
                    icon={<MdOutlineHealthAndSafety size={16} />}
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    options={Object.entries(SOCIAL_REGIMES).map(
                      ([key, value]) => ({
                        value,
                        label: SUBSCRIPTION.FORM.MANDATORY_SCHEME.OPTIONS[key],
                      }),
                    )}
                    label={socialRegimeField.label}
                    placeholder={SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER}
                    defaultValue={socialRegimeField.defaultValue}
                    {...field}
                    onChange={socialRegimeField.onChange}
                    onBlur={socialRegimeField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socialSecurityNumberField.path}
                label={socialSecurityNumberField.label}
                required={socialSecurityNumberField.required}
                disabled={socialSecurityNumberField.disabled}
              >
                {(field) => (
                  <Input
                    label={socialSecurityNumberField.label}
                    icon={<MdOutlineSubtitles size={16} />}
                    type="custom"
                    trim={true}
                    placeholder={
                      SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                    }
                    defaultValue={socialSecurityNumberField.defaultValue}
                    {...field}
                    onChange={socialSecurityNumberField.onChange}
                    onBlur={socialSecurityNumberField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          </Row>
        </Form>
      </div>
    )
  );
};

export default Partner;
